import { ChevronDown, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import ScrollButton, { ScrollPosition } from './ScrollButton'
import TreeNode from './TreeNode'

export interface TreeSelectorChild {
  id: string
  label: string
  disabled?: boolean
}

export interface TreeSelectorParent {
  id: string
  label: string
  children: TreeSelectorChild[]
}

interface TreeSelectorPropsWithTree {
  data: TreeSelectorParent[]
  selectedId?: string
  onSelect: (childId: string, parentId: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  loading?: boolean
  noTree?: false
}

interface TreeSelectorPropsFlat {
  data: TreeSelectorChild[]
  selectedId?: string
  onSelect: (childId: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  loading?: boolean
  noTree: true
}

type TreeSelectorProps = TreeSelectorPropsWithTree | TreeSelectorPropsFlat

export const TreeSelector = (props: TreeSelectorProps) => {
  const {
    data,
    selectedId,
    onSelect,
    placeholder = 'Select an option',
    className,
    disabled = false,
    loading = false,
    noTree = false,
  } = props

  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set())
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    isAtTop: true,
    isAtBottom: false,
  })
  const [isOpen, setIsOpen] = useState(false)

  // Find selected item label
  const selectedLabel = useMemo(() => {
    if (!selectedId) return null

    if (noTree) {
      // For flat list, data is TreeSelectorChild[]
      const flatData = data as TreeSelectorChild[]
      const item = flatData.find((child) => child.id === selectedId)
      return item?.label || null
    } else {
      // For tree, data is TreeSelectorParent[]
      const treeData = data as TreeSelectorParent[]
      for (const parent of treeData) {
        const child = parent.children.find((c) => c.id === selectedId)
        if (child) return child.label
      }
      return null
    }
  }, [selectedId, data, noTree])

  // Auto-expand the parent containing the currently selected child (only for tree mode)
  useEffect(() => {
    if (noTree || !selectedId || data.length === 0) return

    const treeData = data as TreeSelectorParent[]
    const parent = treeData.find((p) =>
      p.children.some((c) => c.id === selectedId)
    )

    if (parent && !expandedParents.has(parent.id)) {
      setExpandedParents((prev) => new Set([...prev, parent.id]))
    }
  }, [selectedId, data, expandedParents, noTree])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isAtTop = scrollTop === 0
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1
    setScrollPosition({ isAtTop, isAtBottom })
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    if (open) {
      setScrollPosition({ isAtTop: true, isAtBottom: false })
      const container = document.querySelector('.tree-selector-content')
      if (container) container.scrollTop = 0
    }
  }, [])

  const toggleParentExpansion = useCallback((parentId: string) => {
    setExpandedParents((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(parentId)) {
        newSet.delete(parentId)
      } else {
        newSet.add(parentId)
      }
      return newSet
    })
  }, [])

  const handleChildClick = useCallback(
    (childId: string, parentId?: string) => {
      if (disabled) return
      if (noTree) {
        // For flat mode, onSelect has one parameter
        ;(onSelect as (childId: string) => void)(childId)
      } else {
        // For tree mode, onSelect has two parameters
        ;(onSelect as (childId: string, parentId: string) => void)(
          childId,
          parentId!
        )
      }
      setIsOpen(false) // Close popover on selection
    },
    [onSelect, disabled, noTree]
  )

  const handleScrollUp = useCallback(() => {
    const container = document.querySelector('.tree-selector-content')
    if (container) {
      container.scrollTop -= 100
    }
  }, [])

  const handleScrollDown = useCallback(() => {
    const container = document.querySelector('.tree-selector-content')
    if (container) {
      container.scrollTop += 100
    }
  }, [])

  if (loading) {
    return (
      <Button
        variant="outline"
        className={`justify-start h-8 font-normal w-[200px] ${className || ''}`}
        disabled
      >
        <span className="truncate">Loading...</span>
        <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
      </Button>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`justify-start h-8 font-normal w-[200px] ${className || ''} ${!selectedLabel ? 'text-muted-foreground' : ''}`}
          disabled={disabled}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-0">
        <div className="h-[350px] flex flex-col">
          {!scrollPosition.isAtTop && (
            <ScrollButton direction="up" onClick={handleScrollUp} />
          )}

          <div
            className="flex-1 p-2 overflow-y-auto tree-selector-content [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onScroll={handleScroll}
          >
            {noTree ? (
              // Flat list mode
              <>
                {(data as TreeSelectorChild[]).map((child) => (
                  <TreeNode
                    key={child.id}
                    icon={null}
                    label={child.label}
                    checked={child.id === selectedId}
                    disabled={child.disabled}
                    onCheck={() => handleChildClick(child.id)}
                    onClick={() => handleChildClick(child.id)}
                    className="text-sm mb-1"
                    type="date"
                  />
                ))}
              </>
            ) : (
              // Tree mode
              <>
                {(data as TreeSelectorParent[]).map((parent) => {
                  const isExpanded = expandedParents.has(parent.id)

                  return (
                    <div key={parent.id} className="mb-1">
                      {/* Parent Node - No checkbox, only expands */}
                      <TreeNode
                        icon={
                          isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )
                        }
                        label={parent.label}
                        checked={false}
                        onCheck={() => {}} // No-op for parent
                        onClick={() => toggleParentExpansion(parent.id)}
                        className="text-sm font-medium"
                        type="date"
                        branchOnly={true} // Hides checkbox for parent
                      />

                      {isExpanded && (
                        <div className="ml-10 mt-1">
                          {parent.children.map((child) => (
                            <TreeNode
                              key={child.id}
                              icon={null}
                              label={child.label}
                              checked={child.id === selectedId}
                              disabled={child.disabled}
                              onCheck={() =>
                                handleChildClick(child.id, parent.id)
                              }
                              onClick={() =>
                                handleChildClick(child.id, parent.id)
                              }
                              className="text-sm"
                              type="date"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </>
            )}
          </div>

          {!scrollPosition.isAtBottom && (
            <ScrollButton direction="down" onClick={handleScrollDown} />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
